import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, catchError, filter, from, map, of, switchMap, withLatestFrom } from 'rxjs';

import { DataRepository } from '@core/data/data-repository';
import { TrainingApiService } from '@core/data/api/training-api.service';
import { trainingFromDto } from '@core/data/api/training.mapper';
import { TrainingData } from '@core/models';
import { apiErrorMessage } from '@core/util/api-error.util';
import { ConfirmService } from '@shared/ui/overlay/confirm/confirm.service';
import { HttpErrorResponse } from '@angular/common/http';
import { TrainingApiActions, TrainingPageActions } from './training.actions';
import { trainingFeature } from './training.reducer';

/**
 * Loads the training dataset — from the server's `GET /training` where there is
 * one, the prototype's seed without.
 */
@Injectable()
export class TrainingEffects {
  private readonly actions$ = inject(Actions);
  private readonly repo = inject(DataRepository);
  private readonly store = inject(Store);
  private readonly api = inject(TrainingApiService);
  private readonly confirm = inject(ConfirmService);

  /**
   * Load the training dataset ONCE. Every training page dispatches `entered`, but
   * the repository holds only the seed; reloading on each entry would overwrite
   * in-session edits (e.g. a module just assigned to someone). So we skip the load
   * once the slice is already loaded and let later edits live in the store.
   */
  /** The dataset: the server's where there is one, the seed without. */
  private training$(): Observable<TrainingData> {
    return this.api.available()
      ? serverTraining(this.api, (err) => this.assignmentsFailed(err))
      : from(this.repo.listTraining());
  }

  /** A failed assignments read: quiet for "not allowed", said out loud otherwise. */
  private assignmentsFailed(err: unknown): void {
    if (err instanceof HttpErrorResponse && err.status === 403) return;
    void this.confirm.alert({
      title: 'Training Assignments Did Not Load',
      message: `Who is on each training module could not be read, so every module shows nobody on it. ${apiErrorMessage(err)}`.trim(),
    });
  }

  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrainingPageActions.entered),
      withLatestFrom(this.store.select(trainingFeature.selectLoaded)),
      filter(([, loaded]) => !loaded),
      switchMap(() =>
        this.training$().pipe(
          map((training) => TrainingApiActions.loadSuccess({ training })),
          catchError((err: unknown) =>
            of(TrainingApiActions.loadFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );
}

/**
 * The server's framework, and who is on each module.
 *
 * Assignments are one read of the whole company's. The server answers it only
 * for somebody who may author Training; for anybody else it is refused (403),
 * quietly, and contributes nobody (their own assignments arrive with My
 * Training's wiring) rather than failing the framework with it. Any OTHER
 * failure is said out loud — it silently reading as "nobody is on any module"
 * is how a missing endpoint looked like lost assignments.
 *
 * **Not from the server yet:** progress records (assessed levels, performances)
 * — My Training is the last wiring step, so they start empty and every screen
 * reads "not assessed" until then. And no company-wide cadence rules: each
 * competency carries its own schedule (contract §6), and the Settings tab that
 * set those rules is gone.
 */
function serverTraining(api: TrainingApiService, onAssignmentsFailed: (err: unknown) => void): Observable<TrainingData> {
  return api.framework().pipe(
    switchMap((dto) =>
      api.allAssignments().pipe(
        catchError((err: unknown) => {
          onAssignmentsFailed(err);
          return of([]);
        }),
        map((rows) => {
          const mapped = trainingFromDto(dto, rows);
          return { ...mapped, completions: {}, defaultReviewReps: 5 };
        }),
      ),
    ),
  );
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
